<template>
    <v-card id="petitions" class="card-form-outlined mb-5 section-anchor clean-detail-card proceeding-petitions-card">
        <v-card-title class="card-group-title petition-card-title">
            <div>
                <span>Petições da Administração Judicial</span>
                <small>Geração, versionamento e protocolo das manifestações vinculadas à impugnação.</small>
            </div>
            <v-btn small outlined class="section-primary-action section-compact-action" color="primary" elevation="0" @click="dialog = true">
                <v-icon left size="15">mdi-file-document-edit-outline</v-icon>Gerar manifestação
            </v-btn>
        </v-card-title>

        <v-card-text>
            <v-alert v-if="!loading && !templates.length" dense text color="primary" class="petition-setup-notice">
                <div class="petition-notice-content">
                    <v-icon color="primary" size="19">mdi-information-outline</v-icon>
                    <div>
                        <strong>Estrutura pronta para receber os modelos</strong>
                        <span>Os modelos jurídicos ainda não foram publicados. Assim que forem cadastrados, a geração será liberada nesta mesma tela.</span>
                    </div>
                </div>
            </v-alert>

            <div v-if="loading" class="petition-loading" role="status">
                <v-progress-circular indeterminate color="primary" size="24" width="2" />
                <span>Carregando petições...</span>
            </div>

            <div v-else-if="petitions.length" class="petition-list">
                <article v-for="petition in petitions" :key="petition.id" class="petition-row">
                    <span class="petition-file-icon"><v-icon size="18">mdi-file-pdf-box</v-icon></span>
                    <div class="petition-row-main">
                        <strong>{{ petition.template_name || `Manifestação v${petition.version}` }}</strong>
                        <small>Versão {{ petition.version }} · {{ formatDate(petition.generated_at || petition.created_at) }}</small>
                    </div>
                    <span class="petition-status" :class="`status-${petition.status}`">{{ statusLabel(petition.status) }}</span>
                    <div class="petition-actions">
                        <v-btn v-if="petition.pdf_url" icon small :href="petition.pdf_url" target="_blank" aria-label="Visualizar PDF"><v-icon size="17">mdi-eye-outline</v-icon></v-btn>
                        <v-btn v-if="petition.pdf_url" icon small :href="petition.pdf_url" download aria-label="Baixar PDF"><v-icon size="17">mdi-download</v-icon></v-btn>
                    </div>
                </article>
            </div>

            <div v-else class="petition-empty-state">
                <span class="petition-empty-icon"><v-icon size="26">mdi-file-document-plus-outline</v-icon></span>
                <strong>Nenhuma petição gerada</strong>
                <p>O histórico de versões e protocolos aparecerá aqui.</p>
            </div>
        </v-card-text>

        <v-dialog v-model="dialog" max-width="760" content-class="proceedings-shadcn-dialog petition-foundation-dialog" persistent>
            <v-card>
                <v-card-title class="dialog-header">
                    <div><span>Gerar manifestação</span><small>Selecione um modelo jurídico para iniciar a composição do documento.</small></div>
                    <v-btn icon small aria-label="Fechar" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="petition-dialog-body">
                    <div class="petition-flow-steps">
                        <span class="active"><b>1</b> Modelo e cenário</span>
                        <span><b>2</b> Revisão dos dados</span>
                        <span><b>3</b> Pré-visualização</span>
                    </div>

                    <div class="petition-dialog-section">
                        <label>Modelo da manifestação <em>*</em></label>
                        <v-select v-model="selectedTemplateId" :items="templates" item-value="template_version_id" item-text="name"
                            placeholder="Selecione o modelo" outlined dense hide-details="auto" :disabled="!templates.length" />
                    </div>

                    <v-alert v-if="!templates.length" type="info" text dense class="mt-4">
                        Nenhum modelo foi publicado para Impugnações. O fluxo permanecerá bloqueado até a inclusão da primeira versão homologada.
                    </v-alert>

                    <div class="petition-prepared-data">
                        <strong>Dados que serão aproveitados automaticamente</strong>
                        <div><v-icon size="17">mdi-gavel</v-icon><span>Número do processo, tribunal, vara e comarca</span></div>
                        <div><v-icon size="17">mdi-account-outline</v-icon><span>Manifestantes e créditos vinculados</span></div>
                        <div><v-icon size="17">mdi-history</v-icon><span>Manifestações, decisões e andamento processual</span></div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialog = false">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" :disabled="!selectedTemplateId">Continuar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import axios from "axios";

export default {
    name: "ProceedingPetitions",
    props: {
        proceedingId: { type: [String, Number], required: true },
    },
    data: () => ({
        loading: true,
        dialog: false,
        petitions: [],
        templates: [],
        selectedTemplateId: null,
    }),
    created() {
        this.refresh();
    },
    methods: {
        content(response) {
            return response && response.data && response.data.content !== undefined ? response.data.content : response.data;
        },
        async refresh() {
            this.loading = true;
            try {
                const [petitions, templates] = await Promise.all([
                    axios.get(`/api/admin/proceedings/${this.proceedingId}/petitions`),
                    axios.get("/api/admin/proceeding-petition-templates"),
                ]);
                this.petitions = this.content(petitions) || [];
                this.templates = this.content(templates) || [];
            } catch (_) {
                this.petitions = [];
                this.templates = [];
            } finally {
                this.loading = false;
                this.$emit("count", this.petitions.length);
            }
        },
        formatDate(value) {
            return value ? this.$moment(value).format("DD/MM/YYYY HH:mm") : "data não informada";
        },
        statusLabel(status) {
            return ({ draft: "Rascunho", generated: "Aguardando protocolo", protocolled: "Protocolada", published: "Publicada", cancelled: "Cancelada" })[status] || status;
        },
    },
};
</script>

<style scoped>
.petition-card-title { align-items: center; display: flex; justify-content: space-between; }
.petition-card-title > div { display: flex; flex-direction: column; }
.petition-card-title small { color: #73788a; font-size: 11px; font-weight: 400; margin-top: 3px; }
.petition-setup-notice { border-radius: 8px; }
.petition-notice-content { align-items: flex-start; display: flex; gap: 10px; }
.petition-notice-content div { display: flex; flex-direction: column; gap: 3px; }
.petition-notice-content strong { color: #25275d; font-size: 12px; }
.petition-notice-content span { color: #646a7d; font-size: 11px; }
.petition-loading, .petition-empty-state { align-items: center; color: #73788a; display: flex; flex-direction: column; gap: 7px; justify-content: center; min-height: 170px; text-align: center; }
.petition-empty-icon { align-items: center; background: #f2f4f8; border-radius: 50%; color: #555b70; display: flex; height: 50px; justify-content: center; width: 50px; }
.petition-empty-state strong { color: #3d4155; font-size: 14px; }
.petition-empty-state p { font-size: 12px; margin: 0; }
.petition-list { border: 1px solid #e3e4ed; border-radius: 9px; overflow: hidden; }
.petition-row { align-items: center; display: grid; gap: 12px; grid-template-columns: 36px minmax(0, 1fr) auto 72px; min-height: 64px; padding: 10px 14px; }
.petition-row + .petition-row { border-top: 1px solid #e9eaf0; }
.petition-file-icon { align-items: center; background: #f2f4f8; border-radius: 7px; display: flex; height: 34px; justify-content: center; width: 34px; }
.petition-row-main { display: flex; flex-direction: column; min-width: 0; }
.petition-row-main strong { color: #292c3e; font-size: 12px; }
.petition-row-main small { color: #777c8e; font-size: 10px; margin-top: 3px; }
.petition-status { background: #f0f1f5; border-radius: 999px; color: #62687a; font-size: 9px; font-weight: 650; padding: 5px 9px; white-space: nowrap; }
.petition-status.status-protocolled, .petition-status.status-published { background: #e5f5eb; color: #257146; }
.petition-status.status-generated { background: #fff2d8; color: #9b651d; }
.petition-actions { display: flex; justify-content: flex-end; }
.petition-dialog-body { padding-top: 20px !important; }
.petition-flow-steps { display: grid; gap: 8px; grid-template-columns: repeat(3, 1fr); margin-bottom: 24px; }
.petition-flow-steps span { align-items: center; color: #8a90a3; display: flex; font-size: 10px; gap: 7px; }
.petition-flow-steps b { align-items: center; background: #eef0f4; border-radius: 50%; display: flex; height: 22px; justify-content: center; width: 22px; }
.petition-flow-steps .active { color: #25275d; font-weight: 650; }
.petition-flow-steps .active b { background: #25275d; color: white; }
.petition-dialog-section > label { color: #323647; display: block; font-size: 11px; font-weight: 650; margin-bottom: 6px; }
.petition-dialog-section em { color: #dc2626; font-style: normal; }
.petition-prepared-data { background: #f8f9fb; border: 1px solid #e5e7ed; border-radius: 9px; display: grid; gap: 10px; margin-top: 18px; padding: 14px; }
.petition-prepared-data > strong { color: #34384b; font-size: 11px; }
.petition-prepared-data > div { align-items: center; color: #697083; display: flex; font-size: 10px; gap: 8px; }
@media (max-width: 700px) { .petition-card-title { align-items: stretch; flex-direction: column; gap: 12px; } .petition-flow-steps { grid-template-columns: 1fr; } .petition-row { grid-template-columns: 36px minmax(0, 1fr); } .petition-status, .petition-actions { grid-column: 2; justify-self: start; } }
</style>
